import {
  COMMODITY_TYPES,
  INTELLIGENCE_CONFIG,
  ENDGAME_CONFIG,
} from './constants.js';
import { TradingSystem } from './game-trading.js';
import { EconomicEventsSystem } from './game-events.js';
import { SeededRandom } from './utils/seeded-random.js';

/**
 * InformationBroker - Handles market intelligence purchases and rumor generation
 *
 * Provides players with price data for systems they haven't visited recently,
 * creating a strategic layer around information gathering and trading decisions.
 */
export class InformationBroker {
  /**
   * Calculate intelligence cost for a system based on visit history
   *
   * @param {number} systemId - Target system ID
   * @param {Object} priceKnowledge - Player's price knowledge database
   * @returns {number} Cost in credits
   */
  static getIntelligenceCost(systemId, priceKnowledge) {
    const knowledge = priceKnowledge[systemId];

    // Never visited or orbit-only (no price data)
    if (!knowledge || knowledge.source === INTELLIGENCE_CONFIG.SOURCES.ORBIT) {
      return INTELLIGENCE_CONFIG.PRICES.NEVER_VISITED;
    }

    // Recently visited (within threshold)
    if (knowledge.lastVisit <= INTELLIGENCE_CONFIG.RECENT_THRESHOLD) {
      return INTELLIGENCE_CONFIG.PRICES.RECENT_VISIT;
    }

    // Stale visit (beyond threshold)
    return INTELLIGENCE_CONFIG.PRICES.STALE_VISIT;
  }

  /**
   * Purchase market intelligence for a system
   *
   * Deducts credits and updates price knowledge with current prices.
   * Intelligence data is sometimes unreliable - prices may be manipulated
   * to show false profit opportunities.
   *
   * @param {Object} gameState - Current game state
   * @param {number} systemId - Target system ID
   * @param {Array} starData - Star system data
   * @param {number} discount - Discount fraction (0-1), e.g. 0.15 for 15% off
   * @returns {Object} { success: boolean, reason: string }
   */
  static purchaseIntelligence(gameState, systemId, starData, discount = 0) {
    const priceKnowledge = gameState.world.priceKnowledge || {};
    const credits = gameState.player.credits;

    // Calculate cost
    let cost = InformationBroker.getIntelligenceCost(systemId, priceKnowledge);
    if (discount > 0) {
      cost = Math.ceil(cost * (1 - discount));
    }

    // Validate purchase
    const validation = InformationBroker.validatePurchase(cost, credits);
    if (!validation.valid) {
      return { success: false, reason: validation.reason };
    }

    // Find target system
    const system = starData.find((s) => s.id === systemId);
    if (!system) {
      return { success: false, reason: 'System not found' };
    }

    // Calculate current prices for the system
    const currentDay = gameState.player.daysElapsed;
    const activeEvents = gameState.world.activeEvents || [];
    const currentPrices = {};

    // Use seeded random for deterministic price manipulation
    // Seed includes system ID and day to ensure consistency
    const seed = `intel_${systemId}_${currentDay}`;
    const rng = new SeededRandom(seed);
    const marketConditions = gameState.world.marketConditions || {};

    for (const goodType of COMMODITY_TYPES) {
      let price = TradingSystem.calculatePrice(
        goodType,
        system,
        currentDay,
        activeEvents,
        marketConditions
      );

      // Sometimes the intelligence is unreliable - prices are manipulated
      // to show false profit opportunities
      if (rng.next() < INTELLIGENCE_CONFIG.RELIABILITY.MANIPULATION_CHANCE) {
        const manipulationMultiplier =
          INTELLIGENCE_CONFIG.RELIABILITY.MIN_MANIPULATION_MULTIPLIER +
          rng.next() *
            (INTELLIGENCE_CONFIG.RELIABILITY.MAX_MANIPULATION_MULTIPLIER -
              INTELLIGENCE_CONFIG.RELIABILITY.MIN_MANIPULATION_MULTIPLIER);
        price = Math.round(price * manipulationMultiplier);
      }

      currentPrices[goodType] = price;
    }

    // Deduct credits
    gameState.player.credits -= cost;

    // Update price knowledge
    gameState.world.priceKnowledge[systemId] = {
      lastVisit: 0, // Intelligence is "current"
      prices: currentPrices,
      source: INTELLIGENCE_CONFIG.SOURCES.INTELLIGENCE_BROKER,
    };

    return { success: true, reason: null };
  }

  /**
   * Clean up old intelligence data
   *
   * Removes market data older than INTELLIGENCE_MAX_AGE days to prevent
   * stale information from cluttering the player's knowledge base.
   *
   * lastVisit represents days since last visit and is incremented automatically
   * as time passes, so we just check if it exceeds the threshold.
   *
   * @param {Object} priceKnowledge - Player's price knowledge database
   * @returns {number} Number of systems cleaned up
   */
  static cleanupOldIntelligence(priceKnowledge) {
    let cleanedCount = 0;

    for (const systemId in priceKnowledge) {
      const knowledge = priceKnowledge[systemId];

      if (
        knowledge.lastVisit > INTELLIGENCE_CONFIG.MAX_AGE &&
        knowledge.source !== INTELLIGENCE_CONFIG.SOURCES.ORBIT
      ) {
        delete priceKnowledge[systemId];
        cleanedCount++;
      }
    }

    return cleanedCount;
  }

  /**
   * Generate a market rumor with hints about prices or events
   *
   * Uses seeded random based on current day for deterministic behavior.
   * This ensures rumors are consistent for the same game state, making
   * testing reliable while still providing variety across different days.
   *
   * @param {Object} gameState - Current game state
   * @param {Array} starData - Star system data
   * @param {number} purchaseCount - Number of rumors already purchased (for seed variation)
   * @returns {string} Rumor text
   */
  static generateRumor(gameState, starData, purchaseCount = 0) {
    const currentDay = gameState.player.daysElapsed;
    const activeEvents = gameState.world.activeEvents || [];

    // Use seeded random for deterministic rumor generation
    const seed = `rumor_${currentDay}_${purchaseCount}`;
    const rng = new SeededRandom(seed);

    // Conditional Tanaka hint for mid-game players
    const visitedCount = gameState.world.visitedSystems?.length ?? 0;
    const tanakaFlag = gameState.world.narrativeEvents?.flags?.tanaka_met;
    if (
      !tanakaFlag &&
      visitedCount >= ENDGAME_CONFIG.BARNARDS_ENGINEER_RUMOR_SYSTEMS &&
      rng.next() < ENDGAME_CONFIG.INFO_BROKER_TANAKA_CHANCE
    ) {
      return "There's an engineer at Barnard's Star — Tanaka. Works on drive tech that could push ships beyond the known lanes. Doesn't talk to just anyone, though.";
    }

    // If there are active events, 50% chance to hint about one
    if (activeEvents.length > 0 && rng.next() < 0.5) {
      const marketDisruption = rng.pickRandom(activeEvents);
      const system = starData.find((s) => s.id === marketDisruption.systemId);

      if (system) {
        // Get event type name from the event object or use generic description
        const eventDescriptions = {
          mining_strike: 'labor troubles',
          medical_emergency: 'a health crisis',
          festival: 'celebrations',
        };

        const description =
          eventDescriptions[marketDisruption.type] ||
          'unusual market conditions';
        return `I heard ${system.name} is experiencing ${description}. Might be worth checking out.`;
      }
    }

    // Otherwise, hint about a good price somewhere
    const rumorCommodity = rng.pickRandom(COMMODITY_TYPES);
    const marketConditions = gameState.world.marketConditions || {};

    // Find a system with a good price for this commodity
    let bestSystem = null;
    let bestPrice = Infinity;

    for (const system of starData) {
      const price = TradingSystem.calculatePrice(
        rumorCommodity,
        system,
        currentDay,
        activeEvents,
        marketConditions
      );
      if (price < bestPrice) {
        bestPrice = price;
        bestSystem = system;
      }
    }

    if (bestSystem) {
      return `Word on the street is that ${rumorCommodity} prices are pretty good at ${bestSystem.name} right now.`;
    }

    // Fallback generic rumor
    return `The markets are always changing. Keep your eyes open for opportunities.`;
  }

  /**
   * Validate intelligence purchase
   *
   * @param {number} cost - Intelligence cost
   * @param {number} credits - Player credits
   * @returns {Object} { valid: boolean, reason: string }
   */
  static validatePurchase(cost, credits) {
    if (credits < cost) {
      return {
        valid: false,
        reason: 'Insufficient credits for intelligence',
      };
    }

    return { valid: true, reason: null };
  }

  /**
   * Get all systems with their intelligence costs
   *
   * Used for displaying the information broker interface.
   * Only returns systems connected to the current system via wormholes.
   *
   * When Advanced Sensor Array upgrade is installed, includes active economic
   * events for connected systems.
   *
   * @param {Object} priceKnowledge - Player's price knowledge database
   * @param {Array} starData - Star system data
   * @param {number} currentSystemId - Current system ID
   * @param {Object} navigationSystem - NavigationSystem instance for checking connections
   * @param {Array} activeEvents - Active economic events (optional)
   * @param {boolean} hasAdvancedSensors - Whether Advanced Sensor Array is installed (optional)
   * @returns {Array} Array of { systemId, systemName, cost, lastVisit, event? }
   */
  static listAvailableIntelligence(
    priceKnowledge,
    starData,
    currentSystemId,
    navigationSystem,
    activeEvents = [],
    hasAdvancedSensors = false
  ) {
    // Get systems connected to current system
    const connectedSystemIds =
      navigationSystem.getConnectedSystems(currentSystemId);

    // Filter to only connected systems and map to intelligence data
    return starData
      .filter((system) => connectedSystemIds.includes(system.id))
      .map((system) => {
        const knowledge = priceKnowledge[system.id];
        const cost = InformationBroker.getIntelligenceCost(
          system.id,
          priceKnowledge
        );
        const lastVisit = knowledge ? knowledge.lastVisit : null;

        const result = {
          systemId: system.id,
          systemName: system.name,
          cost: cost,
          lastVisit: lastVisit,
          source: knowledge ? knowledge.source : null,
        };

        // Add event information if Advanced Sensor Array is installed
        if (hasAdvancedSensors && activeEvents.length > 0) {
          const systemEvent = activeEvents.find(
            (event) => event.systemId === system.id
          );
          if (systemEvent) {
            const eventTypeDef = Object.hasOwn(
              EconomicEventsSystem.EVENT_TYPES,
              systemEvent.type
            )
              ? EconomicEventsSystem.EVENT_TYPES[systemEvent.type]
              : null;
            if (eventTypeDef) {
              result.event = {
                name: eventTypeDef.name,
                commodities: Object.keys(systemEvent.modifiers),
                modifiers: systemEvent.modifiers,
              };
            }
          }
        }

        return result;
      });
  }
}
